import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { api } from '@/lib/api';
import { useAuth } from './AuthContext';

interface WeeklyFolder {
  id: string;
  title: string;
  week_start: string;
  week_end: string;
  status: 'active' | 'archived' | 'draft';
  metadata: Record<string, unknown>;
  document_count: number;
  commentary_count: number;
  created_at: string;
  updated_at: string;
}

interface WeeklyFolderContextType {
  folders: WeeklyFolder[];
  currentFolder: WeeklyFolder | null;
  selectedFolder: WeeklyFolder | null;
  isLoading: boolean;
  error: string | null;
  selectFolder: (folder: WeeklyFolder | null) => void;
  refreshFolders: () => Promise<void>;
  refreshCurrentFolder: () => Promise<void>;
  createFolder: (title?: string, weekStart?: string) => Promise<WeeklyFolder>;
  archiveFolder: (id: string) => Promise<void>;
}

const WeeklyFolderContext = createContext<WeeklyFolderContextType | undefined>(undefined);

export function WeeklyFolderProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [folders, setFolders] = useState<WeeklyFolder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<WeeklyFolder | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<WeeklyFolder | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshFolders = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<WeeklyFolder[]>('/weekly-folders');
      if (response.success && response.data) {
        setFolders(response.data);
      } else {
        setError(response.error || 'Failed to load folders');
      }
    } catch (err) {
      setError('Failed to load folders');
      console.error('Error loading folders:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  const refreshCurrentFolder = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await api.get<WeeklyFolder>('/weekly-folders/current');
      if (response.success && response.data) {
        setCurrentFolder(response.data);
        // Auto-select current folder if none selected
        if (!selectedFolder) {
          setSelectedFolder(response.data);
        }
      }
    } catch (err) {
      console.error('Error loading current folder:', err);
    }
  }, [isAuthenticated, selectedFolder]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshFolders();
      refreshCurrentFolder();
    } else {
      setFolders([]);
      setCurrentFolder(null);
      setSelectedFolder(null);
    }
  }, [isAuthenticated, refreshFolders, refreshCurrentFolder]);

  const selectFolder = (folder: WeeklyFolder | null) => {
    setSelectedFolder(folder);
  };

  const createFolder = async (title?: string, weekStart?: string): Promise<WeeklyFolder> => {
    const response = await api.post<WeeklyFolder>('/weekly-folders', {
      title,
      week_start: weekStart,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create folder');
    }

    await refreshFolders();
    return response.data;
  };

  const archiveFolder = async (id: string) => {
    const response = await api.put(`/weekly-folders/${id}/archive`);

    if (!response.success) {
      throw new Error(response.error || 'Failed to archive folder');
    }

    await refreshFolders();

    // Update current folder if it was archived
    if (currentFolder?.id === id) {
      await refreshCurrentFolder();
    }

    // Deselect if archived folder was selected
    if (selectedFolder?.id === id) {
      setSelectedFolder(currentFolder);
    }
  };

  return (
    <WeeklyFolderContext.Provider
      value={{
        folders,
        currentFolder,
        selectedFolder,
        isLoading,
        error,
        selectFolder,
        refreshFolders,
        refreshCurrentFolder,
        createFolder,
        archiveFolder,
      }}
    >
      {children}
    </WeeklyFolderContext.Provider>
  );
}

export function useWeeklyFolders() {
  const context = useContext(WeeklyFolderContext);
  if (context === undefined) {
    throw new Error('useWeeklyFolders must be used within a WeeklyFolderProvider');
  }
  return context;
}
